import type { AmplitudePropertiesMap } from '../../amplitude-properties.types';
import { getMappedEventProperties } from '../../utils';
import { REWARDS_HUB_AMP_EVENT, REWARDS_MODULE } from './event-names';
import type {
    BonusSpinsCardClickedTrackingProps,
    BonusCashCardClickedTrackingProps,
    RewardMachineCardClickedTrackingProps
} from '@fanduel/rewards-hub-client';

export const mapPropNames = (props: Record<string, unknown>) =>
    getMappedEventProperties(props as Partial<AmplitudePropertiesMap>);

export const getRewardBoxTrackingEvents = (
    track: (event: string, props?: Record<string, unknown>) => void,
    parentModule: string
) => {
    return {
        handleRewardsBoxCtaClickError: (props: { rewardId?: string; rewardType?: string }) =>
            track(REWARDS_HUB_AMP_EVENT.REWARDS_BOX_ERROR_CTA_CLICKED, {
                ...mapPropNames(props),
                Module: REWARDS_MODULE.RB_ERROR_MODAL
            }),
        handleRewardsBoxError: (props: { rewardId?: string; rewardType?: string; errorReason: string }) =>
            track(REWARDS_HUB_AMP_EVENT.REWARDS_BOX_ERROR, {
                ...mapPropNames(props),
                Module: REWARDS_MODULE.RB_ERROR_MODAL
            }),
        handleRewardsBoxAnimationClose: (props: {
            rewardId?: string;
            rewardType?: string;
            animationState: 'animation_completed' | 'animation_playing';
            interactionType: 'x_button' | 'background_tap';
        }) =>
            track(REWARDS_HUB_AMP_EVENT.REWARDS_BOX_ANIMATION_CLOSED, {
                ...mapPropNames(props),
                Module: REWARDS_MODULE.RB_ANIMATION
            }),
        handleRewardsBoxOpen: (props: { rewardId?: string; rewardType?: string; rewardAmount?: number }) =>
            track(REWARDS_HUB_AMP_EVENT.REWARDS_BOX_OPENED, {
                ...mapPropNames(props),
                Module: REWARDS_MODULE.RB_ANIMATION
            }),
        handleRewardsBoxClick: (props: {
            rewardId?: string;
            position?: number;
            rewardsCount?: number;
            rewardName?: string;
        }) =>
            track(REWARDS_HUB_AMP_EVENT.REWARDS_MODULE_LINK_CLICKED, {
                ...mapPropNames({ ...props }),
                Module: parentModule
            }),
        handleRewardsBoxCarouselScrolled: (props: { rewardsCount: number }) =>
            track(REWARDS_HUB_AMP_EVENT.REWARDS_BOX_CAROUSEL_SCROLLED, {
                ...mapPropNames(props),
                Module: REWARDS_MODULE.REWARDS_HUB
            })
    };
};

export const getRewardCardTrackingEvents = (track: (event: string, props?: Record<string, unknown>) => void) => {
    return {
        handleRewardModuleViewed: (props: { position?: number; rewardName?: string; rewardId?: string }) =>
            track(REWARDS_HUB_AMP_EVENT.REWARDS_MODULE_VIEWED, { ...mapPropNames(props) }),
        handleRewardModuleError: (props: {
            errorReason?: string;
            position?: number;
            rewardName?: string;
            rewardId?: string;
        }) => track(REWARDS_HUB_AMP_EVENT.REWARDS_MODULE_ERROR, { ...mapPropNames(props) }),
        handleRewardModuleRefreshed: (props: {
            errorReason?: string;
            position?: number;
            rewardName?: string;
            rewardId?: string;
        }) => track(REWARDS_HUB_AMP_EVENT.REWARDS_MODULE_REFRESHED, { ...mapPropNames(props) })
    };
};

export const getCasinoBonusRewardCardTrackingEvents = (
    track: (event: string, props?: Record<string, unknown>) => void
) => {
    return {
        handleBonusCashCardClicked: (props: BonusCashCardClickedTrackingProps) => {
            track(REWARDS_HUB_AMP_EVENT.REWARDS_MODULE_LINK_CLICKED, { ...mapPropNames(props) });
        }
    };
};

export const getBonusSpinsRewardCardTrackingEvents = (
    track: (event: string, props?: Record<string, unknown>) => void
) => {
    return {
        handleBonusSpinsCardClicked: (props: BonusSpinsCardClickedTrackingProps) => {
            track(REWARDS_HUB_AMP_EVENT.REWARDS_MODULE_LINK_CLICKED, { ...mapPropNames(props) });
        }
    };
};

export const getRewardMachineCardTrackingEvents = (track: (event: string, props?: Record<string, unknown>) => void) => {
    return {
        handleRewardMachineCardClicked: (props: RewardMachineCardClickedTrackingProps) => {
            track(REWARDS_HUB_AMP_EVENT.REWARDS_MODULE_LINK_CLICKED, { ...mapPropNames(props) });
        }
    };
};
